<script setup>
import { onMounted, computed, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import { DimensionsIcon, MaximizeIcon, LoaderIcon, ScaleIcon, FileIcon } from 'vue-tabler-icons';
import Table from '@/components/Table.vue';

const store = useStore();
const route = useRoute();
const texture = computed(() => store.getters['textureFiles/currentTextureFile']);
const imageFile = ref();
const fileSize = ref(0);
const fileResolution = ref('- x -');
const loading = ref(false);

onMounted(async () => {
  loading.value = true;
  try {
    await store.dispatch('textureFiles/fetchTextureFileById', route.params.textureId);
  } finally {
    loading.value = false;
  }
});

watch(imageFile, async (value) => {
  if (!value) {
    return;
  }
  fileSize.value = await fetch(value.src)
    .then((r) => r.blob())
    .then((fileImg) => fileImg.size);

  fileResolution.value = `${value.naturalWidth} x ${value.naturalHeight}`;
});

const fullscreenImage = () => {
  if (document.fullscreenElement === imageFile.value.parentElement) {
    return document.exitFullscreen();
  }
  return imageFile.value.parentElement.requestFullscreen();
};
</script>

<template>
  <section class="h-full w-full">
    <div v-if="!loading && texture" class="texture-container">
      <h2 class="texture__name col-span-5 flex flex-grow text-2xl">{{ texture.name }}</h2>
      <div class="col-span-3 flex w-full justify-end">
        <div class="mr-4 flex flex-1 flex-col items-end gap-4 border-r-2 border-gray-200">
          <div class="flex flex-col items-end px-4 py-2">
            <div class="flex items-end gap-x-1 text-sm font-semibold text-gray-500">
              <dimensions-icon class="h-5 w-5"></dimensions-icon>
              <span class="border-b-2 border-dashed border-gray-400">Resolution</span>
            </div>
            <span class="font-semibold text-gray-600">{{ fileResolution }}</span>
          </div>
          <div class="flex flex-col items-end px-4 py-2">
            <div class="flex items-end gap-x-1 text-sm font-semibold text-gray-500">
              <scale-icon class="h-5 w-5"></scale-icon>
              <span class="border-b-2 border-dashed border-gray-400">Image Size</span>
            </div>
            <span class="font-semibold text-gray-600">{{ Math.ceil(fileSize / 1024) }} KB</span>
          </div>
          <div class="flex flex-col items-end px-4 py-2">
            <div class="flex items-end gap-x-1 text-sm font-semibold text-gray-500">
              <file-icon class="h-5 w-5"></file-icon>
              <span class="border-b-2 border-dashed border-gray-400">Image Type</span>
            </div>
            <span class="font-semibold text-gray-600">PNG</span>
          </div>
        </div>
        <div class="texture-container__image">
          <img
            ref="imageFile"
            :src="texture.files.find(({ name }) => name === 'final_desktop').url"
            alt
            class="max-h-full max-w-full"
          />
          <div class="texture-container__image__fullscreen-toggle" @click="fullscreenImage">
            <maximize-icon />
          </div>
        </div>
      </div>

      <div class="col-span-4"></div>

      <Table
        :columns="[
          { name: 'id', title: 'ID' },
          { name: 'version', title: 'Version' },
        ]"
        :rows="[texture]"
        title="Versions"
        class="texture__versions col-span-4"
      ></Table>
    </div>
    <div v-if="loading" class="texture__loader">
      <loader-icon class="animate h-12 w-12 animate-spin" style="animation-duration: 7s" />
    </div>
  </section>
</template>

<style lang="postcss" scoped>
.texture-container {
  @apply relative grid h-full w-full grid-cols-8 items-start gap-6 p-6;
}

.texture-container__image__fullscreen-toggle {
  @apply absolute top-3 right-3 z-20 flex h-8 w-8 items-center justify-center rounded-2xl bg-white bg-opacity-70;
}

.texture__loader {
  @apply flex h-full w-full items-center justify-center bg-black bg-opacity-20 text-4xl text-brand-600;
}

.texture-container__image {
  @apply relative flex flex-1 items-center justify-center;
}
</style>
